import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DescriptionEditor = ({ formData, onChange, errors }) => {
  const [activeTab, setActiveTab] = useState('responsibilities');
  const [showTemplates, setShowTemplates] = useState(false);

  const templates = {
    responsibilities: [
      "Design, develop, and maintain scalable web applications using React and modern JavaScript frameworks",
      "Collaborate with cross-functional teams including designers, product managers, and backend engineers",
      "Write clean, maintainable, and well-documented code following best practices",
      "Participate in code reviews and provide constructive feedback to team members",
      "Optimize application performance and ensure responsive design across devices"
    ],
    requirements: [
      "Bachelor's degree in Computer Science, Engineering, or related field",
      "3+ years of professional experience in software development",
      "Strong proficiency in React, JavaScript/TypeScript, HTML5, and CSS3",
      "Experience with state management libraries (Redux, Context API)",
      "Excellent problem-solving skills and attention to detail"
    ],
    qualifications: [
      "Experience with modern build tools (Webpack, Vite) and version control (Git)",
      "Knowledge of RESTful APIs and asynchronous programming",
      "Familiarity with Agile/Scrum methodologies",
      "Strong communication and teamwork skills",
      "Portfolio of previous work or GitHub profile demonstrating coding skills"
    ]
  };

  const insertTemplate = () => {
    const template = templates?.[activeTab]?.join('\n• ');
    onChange(activeTab, `• ${template}`);
    setShowTemplates(false);
  };

  const tabs = [
    { id: 'responsibilities', label: 'Responsibilities', icon: 'CheckSquare' },
    { id: 'requirements', label: 'Requirements', icon: 'FileText' },
    { id: 'qualifications', label: 'Qualifications', icon: 'Award' }
  ];

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
            <Icon name="FileEdit" size={20} color="var(--color-secondary)" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Job Description</h2>
            <p className="text-sm text-muted-foreground">Detailed information about the role</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName="Sparkles"
          iconPosition="left"
          onClick={() => setShowTemplates(!showTemplates)}
        >
          Use Template
        </Button>
      </div>
      {showTemplates && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3 mb-3">
            <Icon name="Lightbulb" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground mb-2">Template Preview</p>
              <ul className="space-y-1 text-sm text-muted-foreground">
                {templates?.[activeTab]?.map((item, index) => (
                  <li key={index}>• {item}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="default" size="sm" onClick={insertTemplate}>
              Insert Template
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowTemplates(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      <div className="border-b border-border mb-6">
        <div className="flex gap-1">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-smooth border-b-2 ${
                activeTab === tab?.id
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-6">
        {activeTab === 'responsibilities' && (
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Key Responsibilities <span className="text-error">*</span>
            </label>
            <textarea
              value={formData?.responsibilities}
              onChange={(e) => onChange('responsibilities', e?.target?.value)}
              placeholder="• Design and develop web applications&#10;• Collaborate with team members&#10;• Write clean, maintainable code&#10;• Participate in code reviews"
              className="w-full min-h-[200px] px-4 py-3 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-y"
            />
            {errors?.responsibilities && (
              <p className="mt-2 text-sm text-error">{errors?.responsibilities}</p>
            )}
            <p className="mt-2 text-sm text-muted-foreground">
              List the main duties and responsibilities. Use bullet points for clarity.
            </p>
          </div>
        )}

        {activeTab === 'requirements' && (
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Required Qualifications <span className="text-error">*</span>
            </label>
            <textarea
              value={formData?.requirements}
              onChange={(e) => onChange('requirements', e?.target?.value)}
              placeholder="• Bachelor's degree in relevant field&#10;• 3+ years of experience&#10;• Strong technical skills&#10;• Excellent communication abilities"
              className="w-full min-h-[200px] px-4 py-3 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-y"
            />
            {errors?.requirements && (
              <p className="mt-2 text-sm text-error">{errors?.requirements}</p>
            )}
            <p className="mt-2 text-sm text-muted-foreground">
              Specify mandatory qualifications and experience needed for this role.
            </p>
          </div>
        )}

        {activeTab === 'qualifications' && (
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Preferred Qualifications
            </label>
            <textarea
              value={formData?.qualifications}
              onChange={(e) => onChange('qualifications', e?.target?.value)}
              placeholder="• Experience with specific tools or technologies&#10;• Additional certifications&#10;• Industry-specific knowledge&#10;• Leadership experience"
              className="w-full min-h-[200px] px-4 py-3 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-y"
            />
            <p className="mt-2 text-sm text-muted-foreground">
              List nice-to-have skills and qualifications that would make a candidate stand out.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DescriptionEditor;